// src/components/UnifiedHeader.tsx
// Unified header with navigation tabs, user status, and settings

import React from 'react';
import { Settings, Sparkles, Flame } from 'lucide-react';
import { useSoulFile } from '../hooks/useSoulFile';

interface UnifiedHeaderProps {
  activeTab: 'chat' | 'soul-file';
  onTabChange: (tab: 'chat' | 'soul-file') => void;
  onOpenSettings: () => void;
}

const PHASE_CONFIG: Record<string, { label: string; color: string }> = {
  illuminate: { label: 'Illuminate', color: 'bg-amber-100 text-amber-700' },
  release: { label: 'Release', color: 'bg-purple-100 text-purple-700' },
  embody: { label: 'Embody', color: 'bg-pink-100 text-pink-700' },
  integrate: { label: 'Integrate', color: 'bg-cyan-100 text-cyan-700' },
};

export function UnifiedHeader({ activeTab, onTabChange, onOpenSettings }: UnifiedHeaderProps) {
  const { soulFile } = useSoulFile();

  const streak = soulFile?.stats?.currentStreak || 0;
  const phase = soulFile?.journey?.currentPhase || 'illuminate';
  const phaseConfig = PHASE_CONFIG[phase];
  const userName = soulFile?.essence?.preferredName || soulFile?.essence?.displayName;

  return (
    <header className="w-full bg-gradient-to-r from-pink-100/50 via-pink-50/50 to-purple-50/50 backdrop-blur-md">
      <div className="px-6 py-3 flex items-center justify-between">

        {/* Left: Logo + Tabs */}
        <div className="flex items-center gap-6">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-purple-500" />
            <span className="font-semibold text-purple-900 text-lg">Mystara</span>
          </div>

          {/* Tabs */}
          <nav className="flex items-center gap-1">
            <button
              onClick={() => onTabChange('chat')}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                activeTab === 'chat'
                  ? 'bg-white/80 text-purple-900 shadow-sm'
                  : 'text-purple-600 hover:bg-white/40'
              }`}
            >
              Chat
            </button>
            <button
              onClick={() => onTabChange('soul-file')}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                activeTab === 'soul-file'
                  ? 'bg-white/80 text-purple-900 shadow-sm'
                  : 'text-purple-600 hover:bg-white/40'
              }`}
            >
              Soul File
            </button>
          </nav>
        </div>

        {/* Right: User Info + Status + Settings */}
        <div className="flex items-center gap-4">
          {/* User Name */}
          {userName && (
            <span className="text-purple-700 text-sm font-medium hidden md:block">
              {userName}
            </span>
          )}

          {/* Streak - cleaner pill format */}
          {streak > 0 && (
            <div className="flex items-center gap-1 text-purple-700 text-sm bg-white/50 px-2.5 py-1 rounded-full">
              <Flame className="w-4 h-4 text-orange-500" />
              <span className="font-medium">{streak}</span>
            </div>
          )}

          {/* Phase Badge */}
          <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 ${phaseConfig.color}`}>
            <span>✦</span>
            {phaseConfig.label}
          </span>

          {/* Settings */}
          <button
            onClick={onOpenSettings}
            className="p-2 text-purple-600 hover:bg-white/50 rounded-xl transition-colors"
            title="Settings"
          >
            <Settings className="w-5 h-5" />
          </button>
        </div>

      </div>
    </header>
  );
}

export default UnifiedHeader;
